import React, { useState, useEffect } from 'react';

import { Bootpay } from '@bootpay/client-js';
import axios from 'utils/axiosInstance';
import getRootData from 'utils/getRootData';
import { UserInfo } from 'types/user_info';

import { privateDecrypt } from 'crypto';
import { useIdentityVerification } from 'hooks/useIdentityVerification';
//import SubscriptionPaymentPoller from 'utils/payments/subscription_payment_polling';

const VITE_BOOTPAY_JAVASCRIPT_KEY = import.meta.env.VITE_BOOTPAY_JAVASCRIPT_KEY;
const VITE_BOOTPAY_ENABLED = (import.meta.env.VITE_BOOTPAY_ENABLED == '1' || import.meta.env.VITE_BOOTPAY_ENABLED == 'true');
const VITE_SUBSCRIBE_TEST_PAYMENT_ENABLED = (import.meta.env.VITE_SUBSCRIBE_TEST_PAYMENT_ENABLED == '1' || import.meta.env.VITE_SUBSCRIBE_TEST_PAYMENT_ENABLED == 'true');

interface RootData {
  userInfo: UserInfo;
}

interface Plan {
  id: number;
  plan_key: string;
  name: string;
  name_long?: string;
  description?: string;
  price?: number;
  price_feature?: string;
  cycle?: string;
  features?: string[];
}

interface MembershipJoinProps {
  plan_key?: string;
  userInfo?: UserInfo | null;
}

export default function MembershipJoin(props: MembershipJoinProps = {}) {
  const { userInfo: rootUserInfo } = getRootData<RootData>() ?? { userInfo: null };
  const userInfo = props.userInfo || rootUserInfo;

  const plan_key = props.plan_key;
  const [selectedPayment, setSelectedPayment] = useState('card');
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStep, setProcessingStep] = useState('');
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [loading, setLoading] = useState(true);
  const [paymentAmount, setPaymentAmount] = useState('');
  // 첫 결제일: 테스트 결제일 때는 선택 가능, 직접 결제일 때는 오늘 날짜
  const [selectedDate, setSelectedDate] = useState<number>(
    VITE_SUBSCRIBE_TEST_PAYMENT_ENABLED ? 5 : new Date().getDate()
  );

  const isAdmin = userInfo?.roleinfo?.isAllAdminGroup ?? userInfo?.roleinfo?.is_all_admin_group;

  // 세션에서 선택된 member ID 가져오기
  const selectedMemberId = sessionStorage.getItem('selectedMemberId')
    ? parseInt(sessionStorage.getItem('selectedMemberId')!)
    : undefined;

  // 본인인증 훅 사용
  const {
    isVerified,
    isChecking,
    isVerifying,
    error: verificationError,
    status: verificationStatus,
    checkVerificationStatus,
    startVerification,
  } = useIdentityVerification(plan_key, selectedMemberId);

  const DEAFULT_PLAN_DATA: Plan = {
    id: 0,
    plan_key: 'default',
    name: '황금독서클럽 멤버십',
    price: 10000,
    price_feature: 'equal',
    cycle: 'monthly'
  };

  useEffect(() => {
    if (!userInfo || !userInfo.id) {
      alert('로그인이 필요합니다.');
      window.location.href = `/users/sign_in?return_to=/payment/membership${plan_key ? '/' + plan_key : ''}`;
      return;
    }
    if (!plan_key) {
      window.location.href = `/select_plan`;
    }

    // 본인인증 상태 확인
    checkVerificationStatus();
  }, [checkVerificationStatus]);

  useEffect(() => {
    // 플랜 정보 가져오기
    const fetchPlan = async () => {
      if (!plan_key) {
        // plan_key가 없으면 기본 플랜 정보 사용
        setSelectedPlan(DEAFULT_PLAN_DATA);
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`/api/v1/plans?plan_key=${plan_key}`);
        const plans = response.data.plans;
        const plan = plans.find((p: Plan) => p.plan_key === plan_key);

        // console.log('Fetched Plans:', plans);
        // console.log('Selected Plan:', plan);

        if (plan) {
          setSelectedPlan(plan);
          setPaymentAmount(plan.price?.toString() || '');
        } else {
          // 플랜을 찾을 수 없으면 기본값 사용
          setSelectedPlan(DEAFULT_PLAN_DATA);
          setPaymentAmount(DEAFULT_PLAN_DATA.price?.toString() || '');
        }
      } catch (error) {
        console.error('플랜 정보를 가져오는데 실패했습니다:', error);
        // 에러 시에도 기본값 사용
        setSelectedPlan(DEAFULT_PLAN_DATA);
        setPaymentAmount(DEAFULT_PLAN_DATA.price?.toString() || '');
      } finally {
        setLoading(false);
      }
    };

    fetchPlan();
  }, [plan_key]);

  //console.log('User Info:', userInfo);
  //console.log('Selected Plan:', selectedPlan);

  const payMethods = {
    card: '카드',
    //vaccount: '가상계좌',
    //phone: '휴대폰',
    //naverpay: '네이버페이',
  };

  // 결제 금액 검증 함수
  const validatePaymentAmount = (): { isValid: boolean; message?: string } => {
    if (!selectedPlan || !paymentAmount) {
      return { isValid: false, message: '결제 금액을 입력해주세요.' };
    }

    const amount = parseInt(paymentAmount);
    if (isNaN(amount) || amount <= 0) {
      return { isValid: false, message: '올바른 결제 금액을 입력해주세요.' };
    }

    const planPrice = selectedPlan.price || 0;

    switch (selectedPlan.price_feature) {
      case 'equal':
        if (amount !== planPrice) {
          return { isValid: false, message: `결제 금액은 정확히 ${planPrice.toLocaleString()}원이어야 합니다.` };
        }
        break;
      case 'more_than':
        if (amount <= planPrice) {
          return { isValid: false, message: `결제 금액은 ${planPrice.toLocaleString()}원 이상이어야 합니다.` };
        }
        break;
      case 'less_than':
        if (amount > planPrice) {
          return { isValid: false, message: `결제 금액은 ${planPrice.toLocaleString()}원 이하여야 합니다.` };
        }
        break;
    }

    return { isValid: true };
  };

  const handlePayment = async () => {
    // if (!selectedPayment) {
    //   alert('결제 수단을 선택해주세요.');
    //   return;
    // }
    if (!userInfo || !userInfo.id) {
      alert('로그인이 필요합니다.');
      window.location.href = `/users/sign_in?return_to=/payment/membership${plan_key ? '/' + plan_key : ''}`;
      return;
    }
    if (!selectedPlan) {
      alert('플랜 정보를 불러오는 중입니다. 잠시 후 다시 시도해주세요.');
      return;
    }

    // 본인인증 확인
    if (!isVerified) {
      alert('카드 결제를 위해 본인인증이 필요합니다.\n본인인증을 먼저 완료해주세요.');
      return;
    }

    // 결제 금액 검증
    const validation = validatePaymentAmount();
    if (!validation.isValid) {
      alert(validation.message);
      return;
    }

    setIsProcessing(true);
    setProcessingStep('결제 준비 중...');

    if (!VITE_BOOTPAY_ENABLED) {
      alert('현재 결제 시스템이 활성화되어 있지 않습니다. 관리자에게 문의해주세요.');
      setIsProcessing(false);
      return;
    }

    try {
      // 1단계: 백엔드에서 결제 준비
      console.log('Preparing membership join...');
      const prepareResponse = await axios.post('/payment/prepare_membership_join', {
        plan_key: selectedPlan.plan_key,
        subscription_date: selectedDate,
        payment_amount: paymentAmount,
      });

      if (!prepareResponse.data.success) {
        throw new Error(prepareResponse.data.message || '결제 준비 중 오류가 발생했습니다.');
      }

      const { order_id, test_amount, payment_amount, contract_date } = prepareResponse.data;
      console.log('Payment prepared:', { order_id, test_amount, payment_amount, contract_date });

      // 환경변수로 테스트 결제 여부 결정, 기본은 직접 결제
      const amount = VITE_SUBSCRIBE_TEST_PAYMENT_ENABLED ? test_amount : payment_amount;

      // 2단계: Bootpay 정기구독 요청
      setProcessingStep('결제창 실행 중...');
      const orderName = `황금독서클럽 멤버십 ${selectedPlan.name}`;
      const response = await Bootpay.requestSubscription({
        application_id: VITE_BOOTPAY_JAVASCRIPT_KEY,
        pg: 'nicepay',
        order_name: orderName,
        subscription_id: order_id, // 백엔드에서 생성된 order_id 사용
        price: amount,
        tax_free: amount,
        user: {
          id: userInfo.userId?.toString(),
          username: userInfo.name,
          email: userInfo.email,
        },
        extra: {
          subscribe_test_payment: VITE_SUBSCRIBE_TEST_PAYMENT_ENABLED,
          open_type: 'iframe',
          plan_key: selectedPlan.plan_key,
          plan_id: selectedPlan.id,
          subscription_date: selectedDate,
          display_error_result: true, // 결제 실패 이탈을 방지하는 팝업 노출
        }
      });

      console.log('Bootpay Response:', response);

      /* example: 
      Bootpay Response: 
      {
        event: "done"
        gateway_url: "https://gw.bootpay.co.kr"
        metadata: {}
        method: "카드자동(REST)"
        method_origin: "카드자동(REST)"
        method_origin_symbol: "card_rebill_rest"
        method_symbol: "card_rebill_rest"
        pg: "나이스페이먼츠"
        published_at: "2025-09-10T14:55:49+09:00"
        receipt_data: {receipt_id: '68c112e433660537664b2dd6', order_id: 'MBR-silver-1757483630699', price: 1000, tax_free: 1000, cancelled_price: 0, …}
        receipt_id: "68c1126f7f67c6e3852797e3"
        requested_at: "2025-09-10T14:53:51+09:00"
        status: 11
        status_locale: "빌링키발급완료"
        subscription_id: "MBR-silver-1757483630699"
      }
      */

      // 결제 완료 후 폴링으로 가입 완료 확인
      if (response.event === 'done') {
        console.log('Payment completed, starting membership join polling...');
        setProcessingStep('멤버십 가입 처리 중...');

        // 멤버십 가입 완료 상태를 폴링으로 확인
        startMembershipJoinPolling(order_id, selectedPlan, selectedDate);
        return; // 폴링이 상태를 관리하므로 여기서 return
      }
    } catch (e) {
      if (e.event === 'cancel') {
        console.log('결제가 취소되었습니다.');
        setProcessingStep('');
      }
      setIsProcessing(false);
      setProcessingStep('');
    }

    // 결제가 완료되지 않은 경우에만 상태 리셋
    setIsProcessing(false);
    setProcessingStep('');
  };

  // 관리자 본인인증 완료처리 함수
  const handleAdminVerifyComplete = async () => {
    if (!confirm('관리자 권한으로 본인인증을 완료 처리하시겠습니까?')) {
      return;
    }

    try {
      setIsProcessing(true);
      setProcessingStep('관리자 본인인증 처리 중...');

      const response = await axios.post('/payment/admin_complete_identity_verification', {
        plan_key: plan_key
      });

      if (response.data.success) {
        alert('본인인증이 완료 처리되었습니다.');
        // 본인인증 상태 다시 확인
        checkVerificationStatus();
      } else {
        alert(response.data.message || '본인인증 완료 처리에 실패했습니다.');
      }
    } catch (error) {
      console.error('Admin verification error:', error);
      alert('본인인증 완료 처리 중 오류가 발생했습니다.');
    } finally {
      setIsProcessing(false);
      setProcessingStep('');
    }
  };

  // 멤버십 가입 완료 폴링 함수
  const startMembershipJoinPolling = async (orderId: string, plan: Plan, contractDate: number) => {
    const maxAttempts = 30; // 30회 시도 (약 1분)
    let attempts = 0;

    const pollInterval = setInterval(async () => {
      attempts++;
      console.log(`Membership join polling attempt ${attempts}/${maxAttempts}`);

      try {
        // 멤버십 상태 확인 API 호출
        const statusResponse = await axios.get('/api/v1/plans/status');
        const data = statusResponse.data;
        console.log('API Response:', data);
        console.log('Expected plan_key:', plan.plan_key);
        console.log('Received plan_key:', data.membership?.plan_key);
        console.log('Plan object:', plan);
        console.log('Plan key comparison:', data.membership?.plan_key === plan.plan_key);
        console.log('Plan key strict comparison:', JSON.stringify(data.membership?.plan_key) === JSON.stringify(plan.plan_key));

        if (data && data.success == true && data.membership && data.membership.plan_key === plan.plan_key) {
          clearInterval(pollInterval);
          setProcessingStep('가입 완료!');
          setIsProcessing(false);

          // 가입 완료 메시지
          const paymentDateMsg = VITE_SUBSCRIBE_TEST_PAYMENT_ENABLED
            ? `매월 ${contractDate}일`
            : `매월 ${new Date().getDate()}일 (오늘부터)`;
          alert(`🎉 멤버십 가입이 완료되었습니다!\n\n플랜: ${plan.name}\n결제일: ${paymentDateMsg}\n\n정기결제가 자동 등록되었습니다.`);

          // 내 플랜 페이지로 이동
          window.location.href = '/my_plan';
          return;
        }

        if (attempts >= maxAttempts) {
          clearInterval(pollInterval);
          setIsProcessing(false);
          setProcessingStep('');

          // 타임아웃 처리
          alert('멤버십 가입 처리 중입니다.\n잠시 후 내 플랜 페이지에서 확인해주세요.');
          window.location.href = '/my_plan';
          return;
        }
      } catch (error) {
        console.error('Membership status check error:', error);

        if (attempts >= maxAttempts) {
          clearInterval(pollInterval);
          setIsProcessing(false);
          setProcessingStep('');

          alert('멤버십 가입 상태 확인 중 오류가 발생했습니다.\n내 플랜 페이지에서 확인해주세요.');
          window.location.href = '/my_plan';
        }
      }
    }, 2000); // 2초마다 체크
  };
  /////////////////////////////////////////////

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <div className="text-center py-20">
          <div className="loading loading-spinner loading-lg"></div>
          <p className="mt-4 text-lg">플랜 정보를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  if (!selectedPlan) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <div className="text-center py-20">
          <p className="text-lg text-error">플랜 정보를 불러올 수 없습니다.</p>
          <button
            className="btn btn-primary mt-4"
            onClick={() => window.location.href = '/select_plan'}
          >
            플랜 선택으로 돌아가기
          </button>
        </div>
      </div>
    );
  }

  const formatPrice = (price: number | undefined) => {
    if (!price) return '무료';
    return `₩${price.toLocaleString()}`;
  };

  const getPriceDisplay = (plan: Plan) => {
    if (!plan.price) return '무료';

    const priceStr = formatPrice(plan.price);
    const cycleStr = plan.cycle === 'monthly' ? '/월' : plan.cycle === 'yearly' ? '/년' : '';
    const featureStr = plan.price_feature === 'more_than' ? ' 이상' :
      plan.price_feature === 'less_than' ? ' 이하' : '';

    return priceStr + cycleStr + featureStr;
  };

  return (
    <>
      {/* 전체 화면 로딩 오버레이 - 임시로 본인인증 중에는 모든 오버레이 숨김 */}
      {isProcessing && !isVerifying && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 text-center max-w-sm">
            <div className="loading loading-spinner loading-lg w-12 h-12 mb-4 mx-auto text-primary"></div>
            <p className="text-lg font-medium mb-2">
              {processingStep || '결제 처리 중...'}
            </p>
            {processingStep.includes('결제창') && (
              <p className="text-sm text-base-content/60">
                브라우저 팝업이 차단되지 않았는지 확인해주세요
              </p>
            )}
            {processingStep.includes('가입 처리') && (
              <div className="text-sm text-base-content/60 space-y-1">
                <p>멤버십 가입을 처리하고 있습니다</p>
                <p>잠시만 기다려주세요</p>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="max-w-2xl mx-auto p-6 space-y-8 mt-20">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => window.location.href = '/select_plan'}
            className="btn btn-ghost btn-sm"
          >
            ← 회원 구분 가입 화면으로 돌아가기
          </button>
        </div>
        <h1 className="text-3xl font-bold text-center">황금독서클럽 멤버십 가입</h1>

        <div className="card bg-base-100 shadow-md border border-base-200">
          <div className="card-body space-y-3">
            <div className="flex justify-between items-center">
              <h2 className="font-semibold text-lg">{selectedPlan.name_long || selectedPlan.name}</h2>
              <span className="font-bold text-lg">{getPriceDisplay(selectedPlan)}</span>
            </div>
            {selectedPlan.description && (
              <p className="text-sm text-base-content/80">{selectedPlan.description}</p>
            )}
            {selectedPlan.features && selectedPlan.features.length > 0 && (
              <ul className="list-disc pl-5 text-sm space-y-1 text-base-content">
                {selectedPlan.features.map((feature, idx) => (
                  <li key={idx}>{feature}</li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* 본인인증 섹션 */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">본인인증</h3>
          <div className={`card border ${isVerified ? 'border-success bg-success/5' : 'border-warning bg-warning/5'}`}>
            <div className="card-body p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center ${isVerified ? 'bg-success text-success-content' : 'bg-warning text-warning-content'
                    }`}>
                    {isChecking ? (
                      <div className="loading loading-spinner loading-xs"></div>
                    ) : isVerified ? (
                      '✓'
                    ) : (
                      '!'
                    )}
                  </div>
                  <div>
                    <p className="font-medium">
                      {isVerified ? '본인인증 완료' : '본인인증 필요'}
                    </p>
                    {verificationStatus?.verification && (
                      <p className="text-sm text-base-content/60">
                        인증일시: {new Date(verificationStatus.verification.authenticated_at).toLocaleString('ko-KR')}
                      </p>
                    )}
                  </div>
                </div>

                {!isVerified && (
                  <div className="flex gap-2">
                    <button
                      className={`btn btn-sm ${isVerifying ? 'btn-disabled loading' : 'btn-warning'}`}
                      onClick={startVerification}
                      disabled={isVerifying}
                    >
                      {isVerifying ? '본인인증 진행 중...' : '본인인증 시작'}
                    </button>
                  </div>
                )}
              </div>

              {verificationError && (
                <div className="alert alert-error text-sm mt-3">
                  <span>{verificationError}</span>
                </div>
              )}

              {!isVerified && (
                <div className="text-sm text-base-content/60 mt-2">
                  💳 카드 결제를 위해 휴대폰 본인인증이 필요합니다.
                </div>
              )}
            </div>
          </div>
        </div>

        {/* 관리자 전용 본인인증 완료처리 버튼 */}
        {isAdmin && (
          <button
            className="btn btn-xs"
            onClick={handleAdminVerifyComplete}
            disabled={isVerifying}
          >
            본인인증완료처리(관리자 전용)
          </button>
        )}

        <div className="space-y-4">
          <h3 className="text-lg font-semibold">결제 정보 입력</h3>

          <div className="form-control">
            <label className="label">
              <span className="label-text font-medium">결제 금액</span>
              <span className="label-text-alt text-xs px-10">
                {selectedPlan.price_feature === 'equal' && '고정 금액'}
                {selectedPlan.price_feature === 'more_than' && `${selectedPlan.price?.toLocaleString()}원 이상`}
                {selectedPlan.price_feature === 'less_than' && `${selectedPlan.price?.toLocaleString()}원 이하`}
              </span>
            </label>
            <input
              type="number"
              className={`input input-bordered w-full ${selectedPlan.price_feature === 'equal'
                ? 'bg-gray-100 text-gray-600 cursor-not-allowed'
                : ''
                }`}
              placeholder={selectedPlan.price_feature === 'equal' ? '' : "결제할 금액을 입력하세요"}
              value={paymentAmount}
              onChange={(e) => setPaymentAmount(e.target.value)}
              min={selectedPlan.price || 0}
              step={selectedPlan.plan_key === 'gold' || selectedPlan.price_feature === 'more_than' ? 5000 : 1000}
              readOnly={selectedPlan.price_feature === 'equal'}
              disabled={selectedPlan.price_feature === 'equal'}
            />
          </div>

          {/* 테스트 결제 모드일 때만 첫 결제일 선택 UI 표시 */}
          {VITE_SUBSCRIBE_TEST_PAYMENT_ENABLED && (
            <div className="form-control">
              <label className="label">
                <span className="label-text font-medium">첫 결제일 선택</span>
                <span className="label-text-alt text-xs px-10">매월 정기결제일이 됩니다</span>
              </label>
              <select
                className="select select-bordered w-full"
                value={selectedDate}
                onChange={(e) => setSelectedDate(parseInt(e.target.value))}
              >
                <option value={5}>매월 5일</option>
                <option value={15}>매월 15일</option>
                <option value={25}>매월 25일</option>
              </select>
              <div className="label">
                <span className="label-text-alt text-xs text-base-content/60">
                  선택하신 날짜부터 매월 정기결제가 진행됩니다.
                </span>
              </div>
            </div>
          )}

          {/* 직접 결제 모드일 때는 오늘 날짜가 첫 결제일임을 안내 */}
          {!VITE_SUBSCRIBE_TEST_PAYMENT_ENABLED && (
            <div className="form-control">
              <div className="alert alert-info">
                <div>
                  <h3 className="font-bold">첫 결제일 안내</h3>
                  <div className="text-sm">
                    오늘({new Date().toLocaleDateString('ko-KR')})이 첫 결제일이 됩니다. 이후 매월 {new Date().getDate()}일에 정기결제가 진행됩니다.
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="form-control">
            <label className="label">
              <span className="label-text font-medium">결제 수단</span>
            </label>
            <div className="flex flex-wrap gap-2 mt-2">
              {Object.entries(payMethods).map(([key, method]) => (
                <button
                  key={key}
                  className={`btn btn-outline btn-sm ${selectedPayment === key ? 'btn-active' : ''}`}
                  onClick={() => setSelectedPayment(key)}
                >
                  {method}
                </button>
              ))}
            </div>
          </div>
          <p className="text-sm text-base-content/60">
            {/* * 쿠폰으로 가입하는 경우에도 다음 결제를 위해 <span className="font-semibold">카드 등록이 필요</span>합니다. */}
            * 황금독서클럽 멤버십은 매월 정기결제되기 때문에 카드 등록이 필요합니다. <br />
            {/* * 결제 금액은 부가세 포함 금액입니다.  */}
          </p>
        </div>

        <div className="divider"></div>

        <div className="space-y-2">
          <h3 className="text-lg font-semibold">결제 정보</h3>
          <div className="text-sm space-y-1">
            <div className="flex justify-between">
              <span>결제 금액</span>
              <span>{paymentAmount ? `₩${parseInt(paymentAmount).toLocaleString()}` : '₩0'}</span>
            </div>
            <div className="flex justify-between">
              <span>결제일</span>
              <span>
                {VITE_SUBSCRIBE_TEST_PAYMENT_ENABLED
                  ? `매월 ${selectedDate}일`
                  : `매월 ${new Date().getDate()}일 (오늘부터)`
                }
              </span>
            </div>
            <div className="flex justify-between">
              <span>쿠폰 사용</span>
              <span>- 0원</span>
            </div>
            <div className="flex justify-between font-bold text-error pt-2 border-t border-base-200">
              <span>총 결제 금액</span>
              <span>{paymentAmount ? `₩${parseInt(paymentAmount).toLocaleString()}` : '₩0'}</span>
            </div>
          </div>
        </div>

        <div className="pt-4 space-y-2">
          <button
            className={`btn w-full text-lg ${isProcessing || isVerifying || !isVerified ? 'btn-disabled' : 'btn-primary'
              } ${isProcessing || isVerifying ? 'loading' : ''}`}
            onClick={handlePayment}
            disabled={isProcessing || isVerifying || !isVerified}
          >
            {(isProcessing || isVerifying) && (
              <span className="loading loading-spinner loading-sm mr-2"></span>
            )}
            {isVerifying
              ? '본인인증 진행 중...'
              : isProcessing
                ? (processingStep || '결제 처리 중... 잠시만 기다려주세요')
                : !isVerified
                  ? '본인인증 후 결제 가능'
                  : '구독결제서비스 등록'
            }
          </button>

          <p className="text-xs text-base-content/60 text-center">
            주문 내용을 확인했으며, 위 내용에 모두 동의합니다.
          </p>
        </div>

        <div className="text-sm text-base-content/50 space-y-1 mb-20">
          <p>* 황금독서클럽 멤버십은 선택하신 결제일을 기준으로 매월 정기결제되는 상품입니다.</p>
          {VITE_SUBSCRIBE_TEST_PAYMENT_ENABLED ? (
            <p>* 구독결제서비스 등록시 1,000원이 결제되며 잠시 후 취소됩니다. 이후 매달 결제일에 자동결제가 진행됩니다.</p>
          ) : (
            <p>* 첫 결제는 구독 등록과 함께 즉시 진행되며, 이후 매월 결제일에 자동결제가 진행됩니다.</p>
          )}
          {/* <p>* 회원 가입 이후 자유 구매는 멤버십에서만 가능합니다.</p> */}
        </div>
      </div>
    </>
  );
}
